import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Tabs,
    Tab,
    Spinner,
    DateRangePicker,
    Input,
    Autocomplete,
    AutocompleteItem,
} from "@heroui/react";
import { LuSearch } from "react-icons/lu";
import { LuPrinter } from "react-icons/lu";
import { parseDate } from "@internationalized/date";
import { useFetchData, useDebounce } from "@/hooks";
import { PageHeader, ThemedText, ErrorState } from "@/components/ui";
import { formatCurrency, formatDate } from "@/utils";

const TableReport = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const selectedTab = searchParams.get("tab") || "this_month";
    const todayStr = new Date().toISOString().split("T")[0];
    const customRange = {
        start: parseDate(searchParams.get("start") || todayStr),
        end: parseDate(searchParams.get("end") || todayStr),
    };
    const selectedCashId = searchParams.get("cash_id") || "";

    // description search: local state for input UX, synced to URL via debounce
    const [descriptionSearch, setDescriptionSearch] = useState(
        searchParams.get("description") || "",
    );
    const debouncedDescription = useDebounce(descriptionSearch, 500);

    useEffect(() => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (debouncedDescription) next.set("description", debouncedDescription);
            else next.delete("description");
            return next;
        });
    }, [debouncedDescription]);

    const updateParams = (updates) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            Object.entries(updates).forEach(([k, v]) => {
                if (v) next.set(k, v);
                else next.delete(k);
            });
            return next;
        });
    };

    // Fetch Master Cash for Filter
    const { data: masterCashes } = useFetchData("select/master-cash");

    const formatDateParam = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    };

    const getReportParams = () => {
        const today = new Date();
        const base = {
            master_cash_id: selectedCashId || undefined,
            description: debouncedDescription || undefined,
        };

        if (selectedTab === "this_month") {
            return base;
        }

        if (selectedTab === "last_month") {
            const start = new Date(
                today.getFullYear(),
                today.getMonth() - 1,
                1,
            );
            const end = new Date(today.getFullYear(), today.getMonth(), 0);
            return {
                ...base,
                start_date: formatDateParam(start),
                end_date: formatDateParam(end),
            };
        }

        if (selectedTab === "2_months_ago") {
            const start = new Date(
                today.getFullYear(),
                today.getMonth() - 2,
                1,
            );
            const end = new Date(today.getFullYear(), today.getMonth() - 1, 0);
            return {
                ...base,
                start_date: formatDateParam(start),
                end_date: formatDateParam(end),
            };
        }

        if (selectedTab === "custom") {
            return {
                ...base,
                start_date: customRange.start.toString(),
                end_date: customRange.end.toString(),
            };
        }

        return base;
    };

    const { data, isLoading, error, refetch } = useFetchData(
        `report/monthly-cash-flow`,
        getReportParams(),
    );

    const reportData = data?.transactions || [];
    const openingBalance = data?.opening_balance || 0;
    const closingBalance = data?.closing_balance || 0;
    const period = data?.period || {};
    const totals = data?.totals || { debit: 0, credit: 0 };

    if (error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Laporan"
                    description="Terjadi kesalahan saat memuat data laporan arus kas."
                    onAction={refetch}
                />
            </div>
        );
    }

    const classes = {
        table: "border-default-300 w-full min-w-max border-separate border-spacing-0 border-[0.5px] text-xs",
        thead: "bg-default-100/50 text-default-600",
        th: "border-default-300 border-[0.5px] p-2 text-center font-semibold",
        // Sticky Header Classes
        // Row 1 (Top: 0)
        thSticky:
            "border-default-300 border-[0.5px] border-t p-2 text-center font-semibold sticky top-0 z-30 bg-content1 whitespace-nowrap",
        // Row 2 (Top: ~33px) - Opening Balance
        thStickyLeft2:
            "border-default-300 border-[0.5px] p-2 text-left sticky top-[33px] z-20 bg-content1 whitespace-nowrap",
        thStickyRight2:
            "border-default-300 border-[0.5px] p-2 text-right sticky top-[33px] z-20 bg-content1 whitespace-nowrap",
        // Row 3 (Top: ~66px) - Totals
        thStickyLeft3:
            "border-default-300 border-[0.5px] p-2 text-left sticky top-[66px] z-20 bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-400 whitespace-nowrap",
        thStickyRight3:
            "border-default-300 border-[0.5px] p-2 text-right sticky top-[66px] z-20 bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-400 whitespace-nowrap",

        thLeft: "border-default-300 border-[0.5px] p-2 text-left",
        thRight: "border-default-300 border-[0.5px] p-2 text-right",
        td: "border-default-300 border-[0.5px] p-2",
        tdCenter: "border-default-300 border-[0.5px] p-2 text-center",
        tdRight: "border-default-300 border-[0.5px] p-2 text-right",
        rowHover: "hover:bg-default-50 transition-colors",
    };

    return (
        <div className="flex h-full w-full flex-col gap-4">
            <div className="flex items-center justify-between">
                <PageHeader
                    title="Laporan Arus Kas Bulanan"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Laporan" },
                        { label: "Arus Kas Bulanan" },
                    ]}
                />
                <Button
                    color="default"
                    size="sm"
                    startContent={<LuPrinter />}
                    isDisabled={isLoading || reportData.length === 0}
                    onPress={() => {
                        const p = getReportParams();
                        const qs = new URLSearchParams();
                        if (p.start_date) qs.set("start_date", p.start_date);
                        if (p.end_date) qs.set("end_date", p.end_date);
                        if (p.master_cash_id)
                            qs.set("master_cash_id", p.master_cash_id);
                        if (p.description) qs.set("description", p.description);
                        navigate(
                            `/cash-flow-monthly-report/print?${qs.toString()}`,
                        );
                    }}
                >
                    Print
                </Button>
            </div>

            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                    <Tabs
                        aria-label="Date Filter"
                        selectedKey={selectedTab}
                        onSelectionChange={(key) => updateParams({ tab: key })}
                        variant="solid"
                        size="sm"
                    >
                        <Tab key="this_month" title="Bulan Ini" />
                        <Tab key="last_month" title="Bulan Lalu" />
                        <Tab key="2_months_ago" title="2 Bulan Lalu" />
                        <Tab key="custom" title="Custom" />
                    </Tabs>

                    {selectedTab === "custom" && (
                        <DateRangePicker
                            aria-label="Pilih Periode"
                            className="max-w-xs"
                            variant="flat"
                            size="sm"
                            value={customRange}
                            onChange={(range) =>
                                updateParams({
                                    start: range.start.toString(),
                                    end: range.end.toString(),
                                })
                            }
                        />
                    )}
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <Input
                        aria-label="Cari Keterangan"
                        placeholder="Cari keterangan..."
                        variant="flat"
                        size="sm"
                        className="max-w-50"
                        startContent={<LuSearch className="text-default-400" />}
                        value={descriptionSearch}
                        onValueChange={setDescriptionSearch}
                        isClearable
                        onClear={() => {
                            setDescriptionSearch("");
                            updateParams({ description: "" });
                        }}
                    />

                    <Autocomplete
                        aria-label="Filter Dompet"
                        placeholder="Semua Kas"
                        variant="flat"
                        size="sm"
                        className="max-w-50"
                        selectedKey={selectedCashId || null}
                        onSelectionChange={(key) => updateParams({ cash_id: key || "" })}
                        isClearable
                        onClear={() => updateParams({ cash_id: "" })}
                    >
                        {(masterCashes || []).map((cash) => (
                            <AutocompleteItem key={cash.value}>{cash.title}</AutocompleteItem>
                        ))}
                    </Autocomplete>
                </div>
            </div>

            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="flex flex-col items-start gap-0.5 px-4 pt-4 pb-2">
                    <ThemedText size="md" className="font-semibold">
                        Laporan Arus Kas
                    </ThemedText>
                    <ThemedText size="xs" variant="caption">
                        Periode: {period.start || "-"} s/d {period.end || "-"}
                    </ThemedText>
                </CardHeader>
                <CardBody className="max-h-[73vh] overflow-auto px-4 pt-2 pb-4">
                    {isLoading ? (
                        <div className="flex h-48 w-full items-center justify-center">
                            <Spinner label="Memuat data..." color="primary" />
                        </div>
                    ) : (
                        <table className={classes.table}>
                            <thead className={classes.thead}>
                                <tr className="bg-default-100 text-default-600">
                                    <th className={classes.thSticky}>
                                        Tgl. Transaksi
                                    </th>
                                    <th className={classes.thSticky}>
                                        Nama Kas
                                    </th>
                                    <th className={classes.thSticky}>
                                        Keterangan
                                    </th>
                                    <th className={classes.thSticky}>Debet</th>
                                    <th className={classes.thSticky}>Kredit</th>
                                    <th className={classes.thSticky}>Saldo</th>
                                </tr>
                                {/* Opening Balance Row */}
                                <tr className="bg-default-50/50 text-default-700">
                                    <th
                                        className={classes.thStickyLeft2}
                                        colSpan={3}
                                    >
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            SALDO AWAL
                                        </ThemedText>
                                    </th>
                                    <th className={classes.thStickyRight2}></th>
                                    <th className={classes.thStickyRight2}></th>
                                    <th className={classes.thStickyRight2}>
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatCurrency(openingBalance)}
                                        </ThemedText>
                                    </th>
                                </tr>

                                {/* Summary Row */}
                                <tr className="bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-400 font-medium">
                                    <th
                                        className={classes.thStickyLeft3}
                                        colSpan={3}
                                    >
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                            color="primary"
                                        >
                                            TOTAL PERIODE INI
                                        </ThemedText>
                                    </th>
                                    <th className={classes.thStickyRight3}>
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatCurrency(totals.debit)}
                                        </ThemedText>
                                    </th>
                                    <th className={classes.thStickyRight3}>
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatCurrency(totals.credit)}
                                        </ThemedText>
                                    </th>
                                    <th className={classes.thStickyRight3}>
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatCurrency(closingBalance)}
                                        </ThemedText>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Transactions */}
                                {reportData.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            className="border-default-300 text-default-400 p-8 text-center"
                                        >
                                            Tidak ada transaksi pada periode ini
                                        </td>
                                    </tr>
                                ) : (
                                    reportData.map((item, index) => (
                                        <tr
                                            key={index}
                                            className={classes.rowHover}
                                        >
                                            <td className={classes.tdCenter}>
                                                <ThemedText size="xs">
                                                    {formatDate(
                                                        item.transaction_date,
                                                        "dd MMM yyyy",
                                                    )}
                                                </ThemedText>
                                            </td>
                                            <td className={classes.td}>
                                                <ThemedText size="xs">
                                                    {item.cash_name}
                                                </ThemedText>
                                            </td>
                                            <td className={classes.td}>
                                                <div className="flex flex-col">
                                                    <ThemedText
                                                        size="xs"
                                                        className="line-clamp-2"
                                                    >
                                                        {item.description ||
                                                            item.category_name}
                                                    </ThemedText>
                                                    {item.is_transfer && (
                                                        <ThemedText
                                                            size="tiny"
                                                            variant="caption"
                                                            className="italic"
                                                        >
                                                            {item.is_income
                                                                ? `(Dari: ${item.transfer_source_cash_name || "Unknown"})`
                                                                : `(Ke: ${item.transfer_dest_cash_name || "Unknown"})`}
                                                        </ThemedText>
                                                    )}
                                                </div>
                                            </td>
                                            <td className={classes.tdRight}>
                                                <ThemedText
                                                    size="xs"
                                                    color={
                                                        item.debit > 0
                                                            ? "success"
                                                            : "default"
                                                    }
                                                >
                                                    {item.debit > 0
                                                        ? formatCurrency(
                                                              item.debit,
                                                          )
                                                        : "-"}
                                                </ThemedText>
                                            </td>
                                            <td className={classes.tdRight}>
                                                <ThemedText
                                                    size="xs"
                                                    color={
                                                        item.credit > 0
                                                            ? "danger"
                                                            : "default"
                                                    }
                                                >
                                                    {item.credit > 0
                                                        ? formatCurrency(
                                                              item.credit,
                                                          )
                                                        : "-"}
                                                </ThemedText>
                                            </td>
                                            <td className={classes.tdRight}>
                                                <ThemedText size="xs">
                                                    {formatCurrency(
                                                        item.balance,
                                                    )}
                                                </ThemedText>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default TableReport;
